import { StatsTrackerInterface } from '../../stats/tracker.interface';
import { DataQueueInterface } from '../../data/queue.interface';
import { GtmStatsDataTrackableInterface } from './data/trackable.interface';
import { GtmStatsDataEcommerceImpressionOnScrollInterface } from './data/ecommerce-impression-on-scroll.interface';
import { GtmStatsDataEcommercePromotionImpressionInterface } from './data/ecommerce-promotion-impression.interface';
import { GtmStatsDataEventCtaClickInterface } from './data/event-cta-click.interface';
import { GtmStatsDataEventCustomCtaClickInterface } from './data/event-custom-cta-click.interface';
import { StatsDataAgentInterface } from '../../stats/data/agent.interface';
import { GtmStatsDataEntityInterface } from './data/entity.interface';
import { GtmStatsDataPromotionInterface } from './data/promotion.interface';
import { EntityType } from './types';
import { StatsDebuggerServiceInterface } from '../../../service/stats-debugger/service.interface';
import { StatsPlatformEnum } from '../../stats/platform.enum';
import { StatsEmitterEvent } from '../../stats/emitter.event';
import { StatsGuardServiceInterface } from '../../../service/stats-guard/service.interface';
import { GtmWindowInterface } from '../window.interface';
import { GtmStatsDataVariablesInterface } from './data/variables.interface';

export class GtmStatsTracker implements StatsTrackerInterface {
  /**
   * Which events should be sent by batch ?
   */
  public static SUPPORTED_BATCH_EVENTS: string[] = ['impressionOnScroll', 'promotionImpression'];

  /**
   *
   * @param gtmWindow
   * @param dataQueue Queue handler for entity impression
   * @param statsDebuggerService
   * @param statsGuardService
   */
  constructor(
    private gtmWindow: GtmWindowInterface,
    private dataQueue: DataQueueInterface,
    private statsDebuggerService: StatsDebuggerServiceInterface,
    private statsGuardService: StatsGuardServiceInterface
  ) {}

  /**
   * @inheritDoc
   */
  public pageView(data: GtmStatsDataVariablesInterface): void {
    this.collect(data, StatsEmitterEvent.pageView);
  }

  /**
   * @inheritDoc
   */
  public homePage(data: GtmStatsDataTrackableInterface): void {
    this.collect(data, StatsEmitterEvent.homePage);
  }

  /**
   * @inheritDoc
   */
  public propertyPage(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.propertyPage);
  }

  /**
   * @inheritDoc
   */
  public propertySerp(data: GtmStatsDataTrackableInterface): void {
    this.collect(data, StatsEmitterEvent.propertySerp);
  }

  /**
   * @inheritDoc
   */
  public propertyImpression(data: GtmStatsDataTrackableInterface[]): void {
    this.collectBatch('property', data, StatsEmitterEvent.propertyImpression);
  }

  /**
   * @inheritDoc
   */
  public propertyLoad(data: any): void {
    // Not tracking anything on GTM
  }

  /**
   * @inheritDoc
   */
  public propertyLeadClick(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.propertyLeadClick);
  }

  /**
   * @inheritDoc
   */
  public propertyLeadSend(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.propertyLeadSend);
  }

  /**
   * @inheritDoc
   */
  public propertyClick(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.propertyClick);
  }

  /**
   * @inheritDoc
   */
  public propertySave(data: GtmStatsDataTrackableInterface): void {
    this.collect(data, StatsEmitterEvent.propertySave);
  }

  /**
   * @inheritDoc
   */
  public propertyUnsave(data: GtmStatsDataTrackableInterface): void {
    this.collect(data, StatsEmitterEvent.propertyUnsave);
  }

  /**
   * @inheritDoc
   */
  public propertyImageBrowse(data: GtmStatsDataTrackableInterface): void {
    this.collect(data, StatsEmitterEvent.propertyImageBrowse);
  }

  /**
   * @inheritDoc
   */
  public agentHome(data: GtmStatsDataTrackableInterface): void {
    this.collect(data, StatsEmitterEvent.agentHome);
  }

  /**
   * @inheritDoc
   */
  public agentPage(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.agentPage);
  }

  /**
   * @inheritDoc
   */
  public agentSerp(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.agentSerp);
  }

  /**
   * @inheritDoc
   */
  public agentImpression(data: GtmStatsDataTrackableInterface[]): void {
    this.collectBatch('agent', data, StatsEmitterEvent.agentImpression);
  }

  /**
   * @inheritDoc
   */
  public agentLoad(data: StatsDataAgentInterface): void {
    // Not tracking anything on GTM
  }

  /**
   * @inheritDoc
   */
  public agentLeadClick(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.agentLeadClick);
  }

  /**
   * @inheritDoc
   */
  public agentLeadSend(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.agentLeadSend);
  }

  /**
   * @inheritDoc
   */
  public agentClick(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.agentClick);
  }

  /**
   * @inheritDoc
   */
  public brokerHome(data: GtmStatsDataTrackableInterface): void {
    this.collect(data, StatsEmitterEvent.brokerHome);
  }

  /**
   * @inheritDoc
   */
  public brokerPage(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.brokerPage);
  }

  /**
   * @inheritDoc
   */
  public brokerSerp(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.brokerSerp);
  }

  /**
   * @inheritDoc
   */
  public brokerImpression(data: GtmStatsDataTrackableInterface[]): void {
    this.collectBatch('broker', data, StatsEmitterEvent.brokerImpression);
  }

  /**
   * @inheritDoc
   */
  public brokerLoad(data: any): void {
    // Not tracking anything on GTM
  }

  /**
   * @inheritDoc
   */
  public brokerLeadClick(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.brokerLeadClick);
  }

  /**
   * @inheritDoc
   */
  public brokerLeadSend(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.brokerLeadSend);
  }

  /**
   * @inheritDoc
   */
  public brokerClick(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.brokerClick);
  }

  /**
   * @inheritDoc
   */
  public userPage(data: GtmStatsDataTrackableInterface): void {
    this.collect(data, StatsEmitterEvent.userPage);
  }

  /**
   * @inheritDoc
   */
  public userSignIn(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.userSignIn);
  }

  /**
   * @inheritDoc
   */
  public userSignOut(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.userSignOut);
  }

  /**
   * @inheritDoc
   */
  public userRegister(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.userRegister);
  }

  /**
   * @inheritDoc
   */
  public managerClick(category: string, action: string): void {
    // Not tracking anything on GTM
  }

  /**
   * @inheritDoc
   */
  public ctaClick(data: GtmStatsDataEventCtaClickInterface | GtmStatsDataEventCustomCtaClickInterface): void {
    this.collect(data, StatsEmitterEvent.ctaClick);
  }

  /**
   * @inheritDoc
   */
  public errorPage(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.errorPage);
  }

  /**
   * @inheritDoc
   */
  public myAccountPage(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.myAccountPage);
  }

  /**
   * @inheritDoc
   */
  public buyVsRentCalculatorPage(data: GtmStatsDataTrackableInterface): void {
    this.collect(data, StatsEmitterEvent.buyVsRentCalculatorPage);
  }

  /**
   * @inheritDoc
   */
  public propertyReadmoreClick(data: GtmStatsDataTrackableInterface): void {
    // Not tracking anything on GTM
  }

  /**
   * @inheritDoc
   */
  public propertyLeadClickWhatsapp(data: GtmStatsDataTrackableInterface[]): void {
    this.collect(data, StatsEmitterEvent.propertyLeadClickWhatsapp);
  }

  /**
   * @inheritDoc
   */
  public propertyLeadSendWhatsapp(data: GtmStatsDataTrackableInterface[]): void {
    // Not tracking anything on GTM
  }

  /**
   * Collects event
   */
  private collect(
    data: GtmStatsDataTrackableInterface | GtmStatsDataTrackableInterface[],
    eventName: StatsEmitterEvent
  ): void {
    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.gtm, eventName, data);

    // Guard
    if (this.statsGuardService.isAuthorized() === false) {
      return;
    }

    // Convert to array
    const dataArray = Array.isArray(data) ? data : [data];

    this.gtmWindow.dataLayer.push(...dataArray);
  }

  /**
   * Send collect calls by batch
   *
   * @param entityName
   * @param data
   * @param eventName
   */
  private collectBatch(
    entityName: EntityType,
    data: GtmStatsDataTrackableInterface[],
    eventName: StatsEmitterEvent
  ): void {
    // For each data to track
    data.forEach((d: GtmStatsDataTrackableInterface) => {
      // Event does not support batch
      if (GtmStatsTracker.SUPPORTED_BATCH_EVENTS.indexOf(d.event) === -1) {
        console.error('Event "' + d.event + '" does not support batch tracking');
      }

      // Queue ID per entity per event name
      const queueId = entityName + '-' + d.event;

      // Add data to queue
      this.dataQueue.add(queueId, d);

      // Flush queue
      this.dataQueue
        .flush(queueId)
        .then((dataList: GtmStatsDataTrackableInterface[]) => {
          const iMax = dataList.length;
          let event: GtmStatsDataTrackableInterface;

          // No data
          if (iMax === 0) {
            return;
          }

          // Impression event
          if (d.event === 'impressionOnScroll') {
            let entities: GtmStatsDataEntityInterface[] = [];
            event = <GtmStatsDataEcommerceImpressionOnScrollInterface>dataList[0];

            for (let i = 0; i < iMax; i++) {
              // Merge data
              entities = entities.concat(
                (<GtmStatsDataEcommerceImpressionOnScrollInterface>dataList[i]).ecommerce.impressions
              );
            }

            (<GtmStatsDataEcommerceImpressionOnScrollInterface>event).ecommerce.impressions = entities;

            // Promotion impression event
          } else if (d.event === 'promotionImpression') {
            let promotions: GtmStatsDataPromotionInterface[] = [];
            event = <GtmStatsDataEcommercePromotionImpressionInterface>dataList[0];

            // For each impression
            for (let i = 0; i < iMax; i++) {
              // Merge data
              promotions = promotions.concat(
                (<GtmStatsDataEcommercePromotionImpressionInterface>dataList[i]).ecommerce.promoView.promotions
              );
            }

            (<GtmStatsDataEcommercePromotionImpressionInterface>event).ecommerce.promoView.promotions = promotions;
          }

          // Collect event
          this.collect(event, eventName);
        })
        .catch(() => null); // silently handle rejected flush
    });
  }
}
